import {
  get_active_organization_id,
  get_active_profile_id,
  get_organization_branding,
} from "@/utils/organization_profile";
import {
  build_profile_setup_form_values,
  first_present,
  parse_json_or_null,
} from "./organization_profile_setup_api";

const GOOGLE_MAP_API_KEY = process.env.VUE_APP_GOOGLE_MAP_API_KEY;

function get_location_coordinate(company, key) {
  return (
    first_present([
      Reflect.get(company ?? {}, key),
      Reflect.get(company?.address ?? {}, key),
    ]) ?? null
  );
}

export default {
  get_organization_id() {
    return first_present([
      this.user_details?.organization_id,
      get_active_organization_id(),
      this.company?.id,
      this.company?.company_id,
    ]) || null;
  },
  get_profile_id_for_branding() {
    return first_present([
      get_active_profile_id({ company: this.company?.base_profile ? this.company : null }),
      this.company?.base_profile?.id,
      this.company?.profile_id,
      this.company?.id,
    ]) || null;
  },
  get_branding_context() {
    return ({
      organization_id: this.get_organization_id(),
      profile_id: this.get_profile_id_for_branding(),
      user_id: this.user_details?.user_id,
    });
  },
  prefill_form() {
    const details = this.user_details || {};
    const saved_branding = this.get_saved_branding();
    const defaults = this.get_platform_default_branding();

    Object.assign(
      this.form,
      build_profile_setup_form_values({
        company: this.company || {},
        current_form: this.form,
        defaults,
        details,
        has_cover_file: !!this.cover_file,
        has_logo_file: !!this.logo_file,
        saved_branding,
      })
    );

    this.saved_branding = saved_branding;
    this.address_list = this.get_company_address_values(this.company || {});
    this.latitude = get_location_coordinate(this.company, "latitude");
    this.longitude = get_location_coordinate(this.company, "longitude");
    this.sync_location_from_address();
  },
  preview_file(event, field) {
    const file = event.target.files?.[0];
    if (!file) return;

    const preview_url = URL.createObjectURL(file);

    if (field === "logo_preview") {
      this.logo_file = file;
      this.form.logo_preview = preview_url;
      return;
    }

    if (field === "cover_preview") {
      this.cover_file = file;
      this.form.cover_preview = preview_url;
    }
  },
  handle_logo_drop(event) {
    this.isDraggingLogo = false;

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    this.preview_file({ target: { files: [file] } }, "logo_preview");
  },
  clear_broken_logo_preview() {
    this.form.logo_preview = "";
    this.logo_file = null;
    localStorage.removeItem("organization_logo");
  },
  load_google_places() {
    if (!GOOGLE_MAP_API_KEY) return;

    if (window.google?.maps?.places) {
      this.$nextTick(this.initialize_location_autocomplete);
      return;
    }

    const existing_script = document.getElementById("googleMapsScript");

    if (existing_script) {
      existing_script.addEventListener("load", () => this.$nextTick(this.initialize_location_autocomplete), { once: true });
      return;
    }

    document.head.appendChild(this.create_google_maps_script());
  },
  create_google_maps_script() {
    const script = document.createElement("script");
    script.id = "googleMapsScript";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => this.$nextTick(this.initialize_location_autocomplete), { once: true });
    return script;
  },
  initialize_location_autocomplete() {
    const input = this.$refs.streetAddressInput;

    if (!this.can_initialize_location_autocomplete(input)) return;

    this.organization_address_autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: "za" },
      fields: ["address_components", "formatted_address", "geometry", "name"],
    });
    this.organization_address_input = input;
    this.organization_address_autocomplete.addListener("place_changed", () => this.apply_selected_place(input));
  },
  can_initialize_location_autocomplete(input) {
    const already_initialized = this.organization_address_autocomplete
      && this.organization_address_input === input;

    return Boolean(input && window.google?.maps?.places && !already_initialized);
  },
  apply_selected_place(input) {
    const place = this.organization_address_autocomplete.getPlace();
    if (!place) return;

    this.form.location = place.formatted_address || input.value || "";
    this.set_company_address_from_place(place);
  },
  get_branding_storage_key() {
    return `organization_branding_${this.get_organization_id() || this.user_details?.user_id || "current"}`;
  },
  get_saved_branding() {
    return get_organization_branding(this.get_branding_context())
      || parse_json_or_null(localStorage.getItem(this.get_branding_storage_key()));
  },
  get_branding_payload() {
    return ({
      organization_id: this.get_organization_id(),
      logo_preview: this.form.logo_preview,
      cover_preview: this.form.cover_preview,
      primary_color: this.form.primary_color,
      secondary_color: this.form.secondary_color,
      tertiary_color: this.form.tertiary_color,
    });
  },
  get_base_profile_id() {
    return first_present([
      this.company?.base_profile?.id,
      this.company?.base_profile_id,
      this.company?.profile_id,
      this.get_profile_id_for_branding(),
    ]) || null;
  },
  get_type_of_organization_id() {
    const type = this.company?.type_of_organization;

    if (type && typeof type === "object") {
      return first_present([type.id, type.type_of_organization_id, type.value]);
    }

    return first_present([
      this.company?.type_of_organization_id,
      this.company?.organization_type_id,
      Number.isFinite(Number(type)) ? type : "",
    ]);
  },
};
